import axios from 'axios';

const BASE_URL = process.env.REACT_APP_SERVER_URL || '';

export function normalizeExtractedResponse(data) {
  if (data && typeof data === 'object') {
    if (Object.prototype.hasOwnProperty.call(data, 'company_description')) {
      const val = data.company_description;
      return typeof val === 'string' ? val : JSON.stringify(val, null, 2);
    }
    if (Object.prototype.hasOwnProperty.call(data, 'project_goal') || Object.prototype.hasOwnProperty.call(data, 'evaluation_criteria')) {
      const goal = data.project_goal ?? '';
      const criteria = data.evaluation_criteria ?? [];
      const criteriaText = typeof criteria === 'string' ? criteria : JSON.stringify(criteria, null, 2);
      return `Project Goal:\n${goal}\n\nEvaluation Criteria:\n${criteriaText}`.trim();
    }
    if (Object.prototype.hasOwnProperty.call(data, 'proposed_specialists')) {
      const val = data.proposed_specialists;
      if (Array.isArray(val)) {
        return formatProposedSpecialists(val);
      }
      return typeof val === 'string' ? val : JSON.stringify(val, null, 2);
    }
  }
  return typeof data === 'string' ? data : JSON.stringify(data ?? '', null, 2);
}

// Converts an array of proposed specialists into clean Markdown for display
function formatProposedSpecialists(list) {
  try {
    if (!Array.isArray(list) || list.length === 0) return 'No specialists found.';
    const blocks = list.map((s, idx) => {
      const name = safeText(s?.name) || `Specialist ${idx + 1}`;
      const profile = safeText(s?.profile);
      const availability = safeText(s?.availability);
      const location = safeText(s?.location);
      const workingPattern = safeText(s?.['working pattern'] || s?.working_pattern);
      const rate = safeText(s?.['standard hourly rate'] || s?.hourly_rate);

      const skills = Array.isArray(s?.['relevant skills']) ? s['relevant skills']
        : Array.isArray(s?.skills) ? s.skills
        : [];
      const skillsLine = skills.length ? `Relevant Skills: ${skills.join(', ')}` : '';

      let expBlock = '';
      if (Array.isArray(s?.relevant_experience) && s.relevant_experience.length) {
        const items = s.relevant_experience.map((e) => {
          const company = safeText(e?.company_name);
          const role = safeText(e?.role_name);
          const start = safeText(e?.start_date);
          const end = safeText(e?.end_date);
          const range = [start, end].filter(Boolean).join(' — ');
          const head = [company, role].filter(Boolean).join(' · ');
          return `- ${head}${range ? ` (${range})` : ''}`;
        }).join('\n');
        expBlock = `\n**Relevant Experience**\n${items}`;
      }

      const meta = [
        availability ? `Availability: ${availability}` : '',
        location ? `Location: ${location}` : '',
        workingPattern ? `Working Pattern: ${workingPattern}` : '',
        rate ? `Standard Hourly Rate: ${rate}` : '',
        skillsLine,
      ].filter(Boolean).join('\n');

      return [
        `### ${name}`,
        profile ? profile : '',
        meta ? `\n${meta}` : '',
        expBlock,
      ].filter(Boolean).join('\n\n');
    });
    return blocks.join('\n\n---\n\n');
  } catch (e) {
    return JSON.stringify(list, null, 2);
  }
}

function safeText(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  try { return JSON.stringify(v); } catch { return String(v); }
}

export async function extractSpecialistProposalFileText({ filePath, fileNumber, baseUrl = BASE_URL }) {
  const url = `${baseUrl}/custom/hutcheon-mearns/extract_specialist_proposal_file_text`;
  const payload = { file_path: filePath, file_number: fileNumber };
  const res = await axios.post(url, payload, { withCredentials: true });
  return normalizeExtractedResponse(res.data);
}

// Fetches the raw structured data without normalization. Useful for file 3 UI.
export async function extractSpecialistProposalFileData({ filePath, fileNumber, baseUrl = BASE_URL }) {
  const url = `${baseUrl}/custom/hutcheon-mearns/extract_specialist_proposal_file_text`;
  const payload = { file_path: filePath, file_number: fileNumber };
  const res = await axios.post(url, payload, { withCredentials: true });
  return res.data;
}

function toBackendPath(p) {
  if (!p) return '';
  const s = String(p);
  if (s.startsWith('Home/')) return s; // backend can resolve Home/
  if (s.startsWith('./')) return s;    // relative to working dir
  return `./${s.replace(/^\/+/, '')}`; // default to ./relative
}

export async function finalizeSpecialistProposal({ files, baseUrl = BASE_URL }) {
  const url = `${baseUrl}/custom/hutcheon-mearns/create_specialist_proposal_pptx`;
  const file_names = Array.isArray(files) ? files.map(toBackendPath) : [];
  const payload = { file_names };
  try {
    const res = await axios.post(url, payload, { withCredentials: true });
    // Returns either a string path or an object like { pptx_path }.
    return res.data;
  } catch (err) {
    // Extract the most useful error message from the backend response
    const data = err?.response?.data;
    const status = err?.response?.status;
    let message = '';
    if (data) {
      message = typeof data === 'string' ? data
        : data.error || data.message || data.detail || JSON.stringify(data);
    }
    if (!message) {
      message = err.message || 'Unknown error';
    }
    const wrapped = new Error(message);
    wrapped.status = status;
    wrapped.backendError = message;
    throw wrapped;
  }
}

// Attempts to auto-fix a malformed output file by sending its contents and the error
// back to the backend small-model fixer. The backend is expected to return the corrected
// content. Returns the raw response body.
export async function fixFileStructure({ filePath, fileNumber, errorMessage, originalContent, baseUrl = BASE_URL }) {
  const url = `${baseUrl}/custom/hutcheon-mearns/fix_file_structure`;
  const payload = {
    file_path: filePath,
    file_number: fileNumber,
    error: errorMessage || '',
    original_content: originalContent || '',
  };
  const res = await axios.post(url, payload, { withCredentials: true });
  return res.data;
}

export async function loadSpecialistProposalChatHistory({ chatId, baseUrl = BASE_URL }) {
  const url = `${baseUrl}/custom/hutcheon-mearns/load_specialist_proposal_chat_history/${chatId}`;
  const res = await axios.get(url, { withCredentials: true });
  return res.data;
}

export async function reformatSpecialistProposalAvailabilityFile({ filePath, baseUrl = BASE_URL }) {
  const url = `${baseUrl}/custom/hutcheon-mearns/reformat_specialist_proposal_availability_file`;
  const payload = { file_path: toBackendPath(filePath) };
  const res = await axios.post(url, payload, { withCredentials: true });
  return res.data;
}